import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
	Flex,
	Grid,
	Stat,
	StatHelpText,
	StatLabel,
	StatNumber,
	useColorModeValue,
	Table,
	Tbody,
	Text,
	Th,
	Thead,
	Tr,
	useColorMode,
	Stack,
	Td,
	TableContainer,
	Badge,
	VStack,
	HStack,
	Tooltip,
	Card,
	CardHeader,
	CardBody,
} from "@chakra-ui/react";
import moment from "moment/moment";

import Loader from "../../Components/Loader/Loader";
import TablePagination from "../../Components/Pagination/TablePagination";
import TicketsTableRow from "../../Components/TableRows/TicketsTableRow";
import { selectActiveProperties } from "../../redux/user/userSlice";
import { documentTypes, fullDateFormat } from "../../utils/types";
import { getPropertyAddress } from "../../utils/verifier";

export default function Dashboard() {
	console.time("Dashboard-Total-Render");
	const textColor = useColorModeValue("gray.700", "white");
	const properties = useSelector(selectActiveProperties);
	const { t } = useTranslation();
	const name = useSelector((state) => state.user.user?.name);
	const email = useSelector((state) => state.user.user?.email);
	const phoneNumber = useSelector((state) => state.user.user?.phoneNumber);
	const { colorMode } = useColorMode();
	const [loading, setLoading] = useState(true);
	const [chartsLoading, setChartsLoading] = useState(true);
	console.time("Dashboard-Initial-Data-Processing");
	const securityDeposits = properties?.map((p) => p.securityDeposits).flat();
	const [tickets, setTickets] = useState();
	const [monthlyRevenueChart, setmonthlyRevenueChart] = useState();
	const [monthlyAdministrationFeeChart, setMonthlyAdministrationFeeChart] = useState();
	const previousMonthRevenuePercentage = {
		ron: Math.round(
			(((monthlyRevenueChart?.series[0].data.slice().reverse()[0] * 100) / monthlyRevenueChart?.series[0].data.slice().reverse()[1] - 100) * 100) / 100
		),
		eur: Math.round(
			(((monthlyRevenueChart?.series[1].data.slice().reverse()[0] * 100) / monthlyRevenueChart?.series[1].data.slice().reverse()[1] - 100) * 100) / 100
		),
	};
	console.timeEnd("Dashboard-Initial-Data-Processing");
	const totalRowBg = useColorModeValue("blue.50", "blue.900");
	const borderColor = useColorModeValue("gray.200", "gray.700");
	const headerBg = useColorModeValue("white", "gray.800");

	// Pagination for tickets
	const ITEMS_PER_PAGE = 5;
	const [currentPage, setCurrentPage] = useState(1);
	const totalTickets = tickets?.length || 0;
	const totalPages = Math.ceil(totalTickets / ITEMS_PER_PAGE);
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
	const endIndex = startIndex + ITEMS_PER_PAGE;
	const paginatedTickets = tickets?.slice(startIndex, endIndex);
	console.time("Dashboard-Expiring-Leases-Calculation");
	const expiringLeases = properties
		.map((property) => {
			const lease = property.documents.find((doc) => doc.type === "lease" && doc.active);
			const extension = property.documents.find((doc) => doc.type === "extension" && doc.active);
			if (!lease) return {};
			if (!extension) {
				if (moment(lease.expirationDate).unix() - moment().unix() < 2592000)
					return {
						owner: property.owner.name,
						address: getPropertyAddress(property),
						expirationDate: lease.expirationDate,
						type: "lease",
						propertyId: property.id,
					};
				return {};
			}
			if (moment(extension.expirationDate).unix() - moment().unix() < 2592000)
				return {
					owner: property.owner.name,
					address: getPropertyAddress(property),
					expirationDate: extension.expirationDate,
					type: "extension",
					propertyId: property.id,
				};
			return {};
		})
		.filter((e) => e.address)
		.sort((a, b) => moment(a.expirationDate).unix() - moment(b.expirationDate).unix());
	console.timeEnd("Dashboard-Expiring-Leases-Calculation");

	const expiringDocuments = properties
		.map((property) => {
			const documents = property.documents.filter((doc) => doc.active && doc.expirationDate && documentTypes.map((d) => d.id).includes(doc.type));
			const expDocs = documents.filter(
				(doc) => moment(doc.expirationDate).unix() - moment().unix() < (doc.type.toLowerCase().includes("insurance") ? 604800 : 2592000)
			);
			return expDocs.map((doc) => ({
				owner: property.owner.name,
				address: getPropertyAddress(property),
				expirationDate: doc.expirationDate,
				type: doc.type,
				propertyId: property.id,
			}));
		})
		.flat()
		.filter((e) => e.address)
		.sort((a, b) => moment(a.expirationDate).unix() - moment(b.expirationDate).unix());
	// Pagination for expiring leases
	const LEASES_PER_PAGE = 5;
	const [leasesPage, setLeasesPage] = useState(1);
	const totalLeases = expiringLeases?.length || 0;
	const totalLeasesPages = Math.ceil(totalLeases / LEASES_PER_PAGE);
	const leasesStartIndex = (leasesPage - 1) * LEASES_PER_PAGE;
	const leasesEndIndex = leasesStartIndex + LEASES_PER_PAGE;
	const paginatedLeases = expiringLeases?.slice(leasesStartIndex, leasesEndIndex);

	// Pagination for expiring documents
	const DOCS_PER_PAGE = 5;
	const [docsPage, setDocsPage] = useState(1);
	const totalDocs = expiringDocuments?.length || 0;
	const totalDocsPages = Math.ceil(totalDocs / DOCS_PER_PAGE);
	const docsStartIndex = (docsPage - 1) * DOCS_PER_PAGE;
	const docsEndIndex = docsStartIndex + DOCS_PER_PAGE;
	const paginatedDocs = expiringDocuments?.slice(docsStartIndex, docsEndIndex);

	useEffect(() => {
		const calculateChartData = async () => {
			console.time("Dashboard-UseEffect");
			if (!properties) return;

			// Set initial loading state
			setLoading(true);
			setChartsLoading(true);

			// Calculate expiring items first so the tables can show
			const t = properties
				.map((p) =>
					p.tickets.map((t) => {
						return { ...t, address: getPropertyAddress(p), propertyId: p.id, owner: p.owner };
					})
				)
				.flat()
				.filter((t) => t.status.toLowerCase() !== "done");
			setTickets(t);
			setLoading(false);

			// Use setTimeout to allow the UI to update before heavy calculations
			setTimeout(() => {
				console.time("Dashboard-Revenue-Calculation");
				let mr = { ron: { months: [], revenue: [] }, eur: { months: [], revenue: [] } };
				for (let index = 0; index < 6; index++) {
					const month = moment().subtract(index, "months").format("M");
					mr.ron.months.unshift(moment().subtract(index, "months").format("MMMM"));
					mr.eur.months.unshift(moment().subtract(index, "months").format("MMMM"));
					let sRon = 0;
					let sEur = 0;
					for (let i = 0; i < properties.length; i++) {
						for (let j = 0; j < properties[i].payments.length; j++) {
							if (moment(properties[i].payments[j].paymentDate, "DD-MM-YYY").format("M") === month && properties[i].payments[j].type !== "administrationFee")
								if (properties[i].payments[j].currency === "ron")
									sRon += Number(properties[i].payments[j].amount) * (properties[i].payments[j].paymentDirection === "incoming" ? 1 : -1);
								else if (properties[i].payments[j].currency === "eur")
									sEur += Number(properties[i].payments[j].amount) * (properties[i].payments[j].paymentDirection === "incoming" ? 1 : -1);
						}
					}
					mr.ron.revenue.unshift(Number(sRon).toFixed(2));
					mr.eur.revenue.unshift(Number(sEur).toFixed(2));
				}
				console.timeEnd("Dashboard-Revenue-Calculation");

				console.time("Dashboard-AdminFee-Calculation");
				let admFee = { ron: { months: [], revenue: [] }, eur: { months: [], revenue: [] } };
				for (let index = 0; index < 6; index++) {
					const month = moment().subtract(index, "months").format("M");
					admFee.ron.months.unshift(moment().subtract(index, "months").format("MMMM"));
					admFee.eur.months.unshift(moment().subtract(index, "months").format("MMMM"));
					let sRon = 0;
					let sEur = 0;
					for (let i = 0; i < properties.length; i++) {
						const property = properties[i];
						const ownerBankAccountCurrency = property.owner.bankDetails.currency;

						for (let j = 0; j < properties[i].payments.length; j++) {
							const payment = property.payments[j];
							if (moment(payment.paymentDate, "DD-MM-YYY").format("M") === month && payment.type === "administrationFee")
								if (ownerBankAccountCurrency === "ron") {
									if (payment.currency === "ron") sRon += Number(payment.amount);
									else if (payment.exchange?.currency === "ron") sRon += Number(payment.exchange.amount);
								} else {
									if (payment.currency === "eur") sEur += Number(payment.amount);
									else if (payment.exchange?.currency === "eur") sEur += Number(payment.exchange.amount);
								}
						}
					}
					admFee.ron.revenue.unshift(Number(sRon).toFixed(2));
					admFee.eur.revenue.unshift(Number(sEur).toFixed(2));
				}
				console.timeEnd("Dashboard-AdminFee-Calculation");

				console.time("Dashboard-Chart-Setup");
				setmonthlyRevenueChart({
					options: {
						tooltip: {
							style: {
								backgroundColor: "#ccc !important",
								fontSize: "12px",
								fontFamily: undefined,
							},
							onDatasetHover: {
								style: {
									backgroundColor: "red !important",
									fontSize: "12px",
									fontFamily: undefined,
								},
							},
						},
						xaxis: {
							categories: mr.ron.months,
							show: true,
							labels: {
								show: true,
								style: {
									fontSize: "12px",
								},
							},
							axisTicks: {
								show: false,
							},
						},
						yaxis: {
							show: true,
							color: "#000",
							labels: {
								show: true,
								style: {
									fontSize: "14px",
								},
							},
						},
						grid: {
							show: true,
						},
						dataLabels: {
							enabled: false,
						},
						plotOptions: {
							bar: {
								borderRadius: 4,
								borderRadiusApplication: "end",
								columnWidth: "18px",
							},
						},
						responsive: [
							{
								breakpoint: 768,
								options: {
									plotOptions: {
										bar: {
											borderRadius: 0,
										},
									},
								},
							},
						],
					},
					series: [
						{
							data: mr.ron.revenue,
							name: "ron",
						},
						{
							data: mr.eur.revenue,
							name: "eur",
						},
					],
				});
				setMonthlyAdministrationFeeChart({
					options: {
						tooltip: {
							style: {
								backgroundColor: "#ccc !important",
								fontSize: "12px",
								fontFamily: undefined,
							},
							onDatasetHover: {
								style: {
									backgroundColor: "red !important",
									fontSize: "12px",
									fontFamily: undefined,
								},
							},
						},
						xaxis: {
							categories: mr.ron.months,
							show: true,
							labels: {
								show: true,
								style: {
									fontSize: "12px",
								},
							},
							axisTicks: {
								show: false,
							},
						},
						yaxis: {
							show: true,
							color: "#000",
							labels: {
								show: true,
								style: {
									fontSize: "14px",
								},
							},
						},
						grid: {
							show: true,
						},
						dataLabels: {
							enabled: false,
						},
						plotOptions: {
							bar: {
								borderRadius: 4,
								borderRadiusApplication: "end",
								columnWidth: "18px",
							},
						},
						responsive: [
							{
								breakpoint: 768,
								options: {
									plotOptions: {
										bar: {
											borderRadius: 0,
										},
									},
								},
							},
						],
					},
					series: [
						{
							data: admFee.ron.revenue,
							name: "ron",
						},
						{
							data: admFee.eur.revenue,
							name: "eur",
						},
					],
				});
				console.timeEnd("Dashboard-Chart-Setup");

				setChartsLoading(false);
				console.timeEnd("Dashboard-UseEffect");
			}, 0);
		};

		calculateChartData();
	}, [properties]);
	const getSecurityDepositsAmount = (currency, location) => {
		if (location === "total")
			return securityDeposits.filter((sd) => sd.currency === currency && sd.location !== "returned").reduce((acc, sd) => acc + Number(sd.amount), 0);
		return securityDeposits.filter((sd) => sd.currency === currency && sd.location === location).reduce((acc, sd) => acc + Number(sd.amount), 0);
	};

	const navigate = useNavigate();
	const goToProperty = (id, tab) => {
		navigate(`/properties/view/${id}?tab=${tab}`);
	};
	const getDaysUntilExpiredBadge = (date) => {
		const daysUntilExpired = moment(date).diff(moment(), "days");
		if (daysUntilExpired < 0)
			return (
				<VStack spacing={1} align="start">
					<Text>{moment(date).format(fullDateFormat)}</Text>
					<Badge colorScheme="red">expired</Badge>
				</VStack>
			);
		return (
			<VStack spacing={1} align="start">
				<Text>{moment(date).format(fullDateFormat)}</Text>
				<Badge colorScheme="yellow">{daysUntilExpired} zile</Badge>
			</VStack>
		);
	};
	const hoverColor = useColorModeValue("gray.100", "gray.700");

	// Memoize the table rows to prevent unnecessary re-renders
	const LeaseRow = React.memo(function LeaseRow({ el }) {
		return (
			<Tr
				onClick={() => {
					goToProperty(el.propertyId, "tenants");
				}}
				_hover={{ bg: hoverColor, cursor: "pointer" }}
			>
				<Td whiteSpace="nowrap" borderBottom="1px" borderColor={borderColor}>
					{getDaysUntilExpiredBadge(el.expirationDate)}
				</Td>
				<Td borderBottom="1px" borderColor={borderColor}>
					<Tooltip label={`${el.address} - ${el.owner}`} placement="top">
						<VStack align="stretch" spacing={0}>
							<Text noOfLines={1}>{el.address}</Text>
							<Text fontSize="xs" color="gray.500" noOfLines={1}>
								{el.owner}
							</Text>
						</VStack>
					</Tooltip>
				</Td>
			</Tr>
		);
	});

	const DocumentRow = React.memo(function DocumentRow({ el }) {
		return (
			<Tr
				onClick={() => {
					goToProperty(el.propertyId, "documents");
				}}
				_hover={{ bg: hoverColor, cursor: "pointer" }}
			>
				<Td whiteSpace="nowrap" borderBottom="1px" borderColor={borderColor}>
					{getDaysUntilExpiredBadge(el.expirationDate)}
				</Td>
				<Td borderBottom="1px" borderColor={borderColor}>
					<Text noOfLines={1}>{t(`types.${el.type}`)}</Text>
				</Td>
				<Td borderBottom="1px" borderColor={borderColor}>
					<Tooltip label={`${el.address} - ${el.owner}`} placement="top">
						<VStack align="stretch" spacing={0}>
							<Text noOfLines={1}>{el.address}</Text>
							<Text fontSize="xs" color="gray.500" noOfLines={1}>
								{el.owner}
							</Text>
						</VStack>
					</Tooltip>
				</Td>
			</Tr>
		);
	});

	const result = (
		<React.Fragment>
			<Flex flexDirection="column" marginBottom={4}>
				<Grid templateColumns={{ md: "1fr", lg: "1.6fr 1.6fr 1fr" }} templateRows={{ md: "1fr auto", lg: "1fr" }} gap="24px">
					<Card>
						<CardHeader fontSize="sm" fontWeight="bold" pb=".1rem">
							<HStack spacing={3} justify="space-between">
								<Text>{t("dashboard.expiringLeases")}</Text>
								{expiringLeases.length > 0 && (
									<Badge colorScheme="yellow" fontSize="sm">
										{expiringLeases.length}
									</Badge>
								)}
							</HStack>
						</CardHeader>
						<CardBody>
							<VStack spacing={4} align="stretch">
								{loading ? (
									<Loader text={t("misc.loading")} />
								) : (
									<>
										<TableContainer maxHeight="300px" overflowY="auto">
											<Table size="sm" variant="simple">
												<Thead position="sticky" top={0} bg={headerBg} zIndex={1}>
													<Tr>
														<Th>{t("table.endDate")}</Th>
														<Th>{t("table.address")}</Th>
													</Tr>
												</Thead>
												<Tbody>
													{paginatedLeases?.map((el, index) => (
														<LeaseRow key={index} el={el} />
													))}
													{expiringLeases.length === 0 && (
														<Tr>
															<Td colSpan={2} textAlign="center" color="gray.500">
																{t("table.noData")}
															</Td>
														</Tr>
													)}
												</Tbody>
											</Table>
										</TableContainer>
										{expiringLeases.length > LEASES_PER_PAGE && (
											<TablePagination
												currentPage={leasesPage}
												totalPages={totalLeasesPages}
												totalItems={totalLeases}
												onPageChange={setLeasesPage}
												itemsName={t("dashboard.expiringLeases")}
											/>
										)}
									</>
								)}
							</VStack>
						</CardBody>
					</Card>
					<Card>
						<CardHeader fontSize="sm" fontWeight="bold" pb=".1rem">
							<HStack spacing={3} justify="space-between">
								<Text>{t("dashboard.expiringDocuments")}</Text>
								{expiringDocuments.length > 0 && (
									<Badge colorScheme="red" fontSize="sm">
										{expiringDocuments.length}
									</Badge>
								)}
							</HStack>
						</CardHeader>
						<CardBody>
							<VStack spacing={4} align="stretch">
								{loading ? (
									<Loader text={t("misc.loading")} />
								) : (
									<>
										<TableContainer maxHeight="300px" overflowY="auto">
											<Table size="sm" variant="simple">
												<Thead position="sticky" top={0} bg={headerBg} zIndex={1}>
													<Tr>
														<Th>{t("table.endDate")}</Th>
														<Th>{t("table.type")}</Th>
														<Th>{t("table.address")}</Th>
													</Tr>
												</Thead>
												<Tbody>
													{paginatedDocs?.map((el, index) => (
														<DocumentRow key={index} el={el} />
													))}
													{expiringDocuments.length === 0 && (
														<Tr>
															<Td colSpan={3} textAlign="center" color="gray.500">
																{t("table.noData")}
															</Td>
														</Tr>
													)}
												</Tbody>
											</Table>
										</TableContainer>
										{expiringDocuments.length > DOCS_PER_PAGE && (
											<TablePagination
												currentPage={docsPage}
												totalPages={totalDocsPages}
												totalItems={totalDocs}
												onPageChange={setDocsPage}
												itemsName={t("dashboard.expiringDocuments")}
											/>
										)}
									</>
								)}
							</VStack>
						</CardBody>
					</Card>
					<Card hidden>
						<CardBody>
							<Flex flexDirection="row" align="center" justify="center" w="100%">
								<Stat me="auto">
									<StatLabel fontSize="sm" fontWeight="bold" pb=".1rem">
										{monthlyRevenueChart?.options.xaxis.categories.slice().reverse()[0]} {t("dashboard.revenue")}
									</StatLabel>
									<Stack direction="column">
										<Flex>
											<StatNumber fontSize="lg">{monthlyRevenueChart?.series[0].data.slice().reverse()[0]} RON</StatNumber>
											<StatHelpText
												alignSelf="flex-end"
												justifySelf="flex-end"
												m="0px"
												color={previousMonthRevenuePercentage.ron >= 0 ? "green" : "red"}
												fontWeight="bold"
												ps="3px"
												fontSize="md"
											>
												{previousMonthRevenuePercentage.ron === Number.NEGATIVE_INFINITY ||
												previousMonthRevenuePercentage.ron === Number.POSITIVE_INFINITY ||
												isNaN(previousMonthRevenuePercentage.ron)
													? "100"
													: previousMonthRevenuePercentage.ron}
												%
											</StatHelpText>
										</Flex>
										<Flex>
											<StatNumber fontSize="lg" color={textColor}>
												{monthlyRevenueChart?.series[1].data.slice().reverse()[0]} EUR
											</StatNumber>
											<StatHelpText
												alignSelf="flex-end"
												justifySelf="flex-end"
												m="0px"
												color={previousMonthRevenuePercentage.eur >= 0 ? "green" : "red"}
												fontWeight="bold"
												ps="3px"
												fontSize="md"
											>
												{previousMonthRevenuePercentage.eur === Number.POSITIVE_INFINITY ||
												previousMonthRevenuePercentage.eur === Number.NEGATIVE_INFINITY ||
												isNaN(previousMonthRevenuePercentage.eur)
													? "100"
													: previousMonthRevenuePercentage.eur}
												%
											</StatHelpText>
										</Flex>
									</Stack>
								</Stat>
							</Flex>
						</CardBody>
					</Card>
					<Card hidden>
						<CardHeader fontSize="sm" fontWeight="bold" pb=".1rem">
							{t("dashboard.properties")}
						</CardHeader>
						<CardBody>
							<Stack>
								<Stack direction={"row"}>
									<Text>{t("dashboard.occupied")}:</Text>
									<Text fontWeight={"bold"}>
										{properties.filter((p) => p.tenants.filter((t) => !t.former).length > 0 || p.specs.apartmentFeatures.type === "garage").length}
									</Text>
								</Stack>
								<Stack direction={"row"}>
									<Text>{t("dashboard.empty")}:</Text>
									<Text fontWeight={"bold"}>
										{properties.filter((p) => p.tenants.filter((t) => !t.former).length === 0 && p.specs.apartmentFeatures.type !== "garage").length}
									</Text>
								</Stack>
							</Stack>
						</CardBody>
					</Card>
					<Card>
						<CardHeader fontSize="sm" fontWeight="bold" pb=".1rem">
							{t("dashboard.securityDeposits")}
						</CardHeader>
						<CardBody>
							<TableContainer>
								<Table variant="simple" size="sm">
									<Thead>
										<Tr>
											<Th></Th>
											<Th textAlign="right">EUR</Th>
											<Th textAlign="right">RON</Th>
										</Tr>
									</Thead>
									<Tbody>
										{["custodyCash", "custodyBank", "owner", "total", "returned", "returnedTenant"].map((l, index) => (
											<Tr key={index} fontWeight={l === "total" ? "bold" : "normal"} bg={l === "total" ? totalRowBg : "transparent"}>
												<Td borderBottom="1px" borderColor={borderColor}>
													{t(`types.${l}`)}
												</Td>
												<Td borderBottom="1px" borderColor={borderColor} textAlign="right">
													{Number(getSecurityDepositsAmount("eur", l)).toFixed(2)}
												</Td>
												<Td borderBottom="1px" borderColor={borderColor} textAlign="right">
													{Number(getSecurityDepositsAmount("ron", l)).toFixed(2)}
												</Td>
											</Tr>
										))}
									</Tbody>
								</Table>
							</TableContainer>
						</CardBody>
					</Card>
				</Grid>
			</Flex>
			<Flex flexDirection="column" marginBottom={4}>
				<Grid templateColumns={{ md: "1fr", lg: "1.8fr 1.2fr" }} templateRows={{ md: "1fr auto", lg: "1fr" }} gap="24px">
					<Card p="1.2rem">
						<CardHeader>
							<Text fontSize="xl" fontWeight="bold">
								{t("dashboard.monthlyRevenue")}
							</Text>
						</CardHeader>
						<CardBody style={{ width: "100%" }}>
							{chartsLoading ? (
								<Loader text={t("misc.loading")} />
							) : monthlyRevenueChart ? (
								<Chart
									type="bar"
									options={{
										...monthlyRevenueChart.options,
										xaxis: {
											...monthlyRevenueChart.options.xaxis,
											labels: { ...monthlyRevenueChart.options.xaxis.labels, style: { ...monthlyRevenueChart.options.xaxis.labels.style, colors: textColor } },
										},
										yaxis: {
											...monthlyRevenueChart.options.yaxis,
											labels: { ...monthlyRevenueChart.options.yaxis.labels, style: { ...monthlyRevenueChart.options.yaxis.labels.style, colors: textColor } },
										},
										tooltip: { ...monthlyRevenueChart.options.tooltip, theme: colorMode },
									}}
									series={monthlyRevenueChart.series}
									style={{ width: "100%" }}
									height="250"
								/>
							) : null}
						</CardBody>
					</Card>
					<Card p="1rem">
						<CardHeader>
							<Text fontSize="xl" color={textColor} fontWeight="bold">
								{t("dashboard.administrationFee")}
							</Text>
						</CardHeader>
						<CardBody justifyContent="center" alignItems="center" height="100%">
							{chartsLoading ? (
								<Loader text={t("misc.loading")} />
							) : monthlyAdministrationFeeChart ? (
								<Chart
									type="bar"
									options={{
										...monthlyAdministrationFeeChart.options,
										xaxis: {
											...monthlyAdministrationFeeChart.options.xaxis,
											labels: {
												...monthlyAdministrationFeeChart.options.xaxis.labels,
												style: { ...monthlyAdministrationFeeChart.options.xaxis.labels.style, colors: textColor },
											},
										},
										yaxis: {
											...monthlyAdministrationFeeChart.options.yaxis,
											labels: {
												...monthlyAdministrationFeeChart.options.yaxis.labels,
												style: { ...monthlyAdministrationFeeChart.options.yaxis.labels.style, colors: textColor },
											},
										},
										tooltip: { ...monthlyAdministrationFeeChart.options.tooltip, theme: colorMode },
									}}
									series={monthlyAdministrationFeeChart.series}
									style={{ width: "100%" }}
									height="250"
								/>
							) : null}
						</CardBody>
					</Card>
				</Grid>
			</Flex>

			<Grid marginBottom={4}>
				<Card overflowX={{ sm: "scroll", xl: "hidden" }}>
					<CardHeader>
						<Text fontSize="xl" fontWeight="bold">
							{t("dashboard.openTickets")}
						</Text>
					</CardHeader>
					<CardBody>
						<VStack spacing={4} align="stretch">
							<TableContainer>
								<Table size="sm">
									<Thead>
										<Tr>
											<Th>{t("table.addedDate")}</Th>
											<Th>{t("table.owner")}</Th>
											<Th>{t("table.address")}</Th>
											<Th>{t("table.status")}</Th>
											<Th>{t("table.title")}</Th>
											<Th>{t("table.author")}</Th>
											<Th></Th>
										</Tr>
									</Thead>
									<Tbody>
										{paginatedTickets?.map((t, index) => (
											<TicketsTableRow key={index} row={t} author={{ name: name, phoneNumber, email }} owner={t.owner} />
										))}

										{tickets?.length === 0 && (
											<Tr>
												<Td colSpan={7}>{t("table.noData")}</Td>
											</Tr>
										)}
									</Tbody>
								</Table>
							</TableContainer>
							{tickets?.length > 0 && (
								<TablePagination
									currentPage={currentPage}
									totalPages={totalPages}
									totalItems={totalTickets}
									onPageChange={setCurrentPage}
									itemsName={t("tickets.tickets")}
								/>
							)}
						</VStack>
					</CardBody>
				</Card>
			</Grid>
		</React.Fragment>
	);
	console.timeEnd("Dashboard-Total-Render");
	return result;
}
